/**
 * Auto-save hook for the ZeroConf wizard.
 * - Watches a subset of the wizardStore via selectors.
 * - Debounces mutations and PUTs to /api/zeroconf/configs/{id}.
 * - If configId is null, POSTs first then stores the returned id.
 * - Exposes { status, error, configId } for the top-bar indicator.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { postJSON } from "../../lib/api";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const client = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export function useAutoSave({ configId, getPayload, configName, debounceMs = 1500 }) {
    const [status, setStatus] = useState/* @type SaveStatus */("idle");
    const [error, setError] = useState(null);
    const [id, setId] = useState(configId || null);
    const timer = useRef(null);
    const inflight = useRef(false);
    const dirty = useRef(false);
    const lastSavedRef = useRef(null);

    const save = useCallback(async () => {
        if (inflight.current) {
            dirty.current = true;
            return;
        }
        inflight.current = true;
        setStatus("saving");
        setError(null);
        try {
            const payload = getPayload();
            const fields = {
                name: configName || "Untitled draft",
                vendor: payload.vendor || "",
                model: payload.modello || "",
                current_step: payload.currentStep || 1,
                wizard_state: payload,
                // Phase 7c.4 — surface the linked IPAM client (if any) at the
                // top level so the backend can index configs by client.
                client_id: payload.client_id || null,
                source: payload.source || "blank",
            };
            let res;
            if (!id) {
                res = await postJSON("/zeroconf/configs", fields);
                setId(res.id);
            } else {
                const r = await client.put(`/zeroconf/configs/${id}`, fields);
                res = r.data;
            }
            lastSavedRef.current = Date.now();
            setStatus("saved");
        } catch (e) {
            console.error("[useAutoSave]", e);
            setError(e?.response?.data?.detail || e.message || "Save failed");
            setStatus("error");
        } finally {
            inflight.current = false;
            if (dirty.current) {
                dirty.current = false;
                schedule();
            }
        }
    }, [id, configName, getPayload]);

    const schedule = useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(save, debounceMs);
    }, [save, debounceMs]);

    const trigger = useCallback(() => {
        setStatus("saving");
        schedule();
    }, [schedule]);

    useEffect(() => {
        return () => {
            if (timer.current) clearTimeout(timer.current);
        };
    }, []);

    return { status, error, configId: id, trigger, saveNow: save };
}
